import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { noteValueLabel } from '@/lib/session-labels';
import { cn } from '@/lib/utils';
import { PILL_INK_CLASS, PILL_SHAPE_CLASS } from '@/lib/variable-pill';
import {
  NOYAUX,
  type NoyauCode,
  type SessionInventoryEntry,
  type SessionUnfilledReason,
  type SessionVariableKind,
  noyauOf,
} from '@matter_extractor/shared';

/**
 * The Variables tab of a session (§12.2, issue #172): the inventory of what
 * the FROZEN experience declares - every value `@` could cite (§8.6) except the
 * objectives, whose matter lives in the conversation and in Matière.
 *
 * ## Served, never computed here
 *
 * The server builds the inventory (`SessionDetail.variables`) because it alone
 * reads the frozen experience, and the exports and `get_session` read the same
 * one: two readers of a session, Claude among them, never see two lists. So the
 * order is the served one - filled first in timeline order, then the declared
 * ones left unfilled - and the only thing this component derives is where the
 * first unfilled line starts, to draw the thicker rule above it.
 *
 * ## One table, a stack of cards under 768 px
 *
 * The maquette's media query, and not a second rendering: under `md` the
 * table, its body and its rows turn into blocks and grids, each cell but the
 * name dissolves (`display: contents`) and speaks its column in a `::before`
 * read off `data-label`. Two renderings would put every value twice in the
 * document, and every query of a test or a screen reader would meet it twice.
 *
 * ## The page is a way back to the card
 *
 * « Un clic sur la page bascule sur l'onglet Parcours, fait défiler jusqu'à la
 * carte et la cerne un instant » - the switch, the scroll and the ring belong
 * to `SessionReading`, which holds both tabs; this component only hands over
 * the visit. A page never reached has no card, so it is named in grey and is
 * not a button.
 */

export interface SessionVariablesProps {
  readonly entries: readonly SessionInventoryEntry[];
  /**
   * The retained noyau, when the session has one: what paints `@noyau` if its
   * value names no noyau of the table - the value is a NAME (the contract), the
   * colour and the triplet are the operator's.
   */
  readonly retained: NoyauCode | null;
  /** A page was clicked: the 0-based index of its last visit in `detail.pages`. */
  readonly onOpenPage: (visit: number) => void;
}

/** « Nature », in grey text and without a pill (revue de #172). */
export const VARIABLE_KIND_LABEL: Readonly<Record<SessionVariableKind, string>> = {
  field: 'champ',
  checkbox: 'case',
  choice: 'choix',
  noyau: 'noyau',
  selection: 'sélection',
  note: 'note',
};

/** The five reasons of §12.2, in the words of its table. */
export const UNFILLED_REASON_LABEL: Readonly<Record<SessionUnfilledReason, string>> = {
  'page-never-reached': 'page jamais atteinte',
  'page-current': 'page en cours',
  'hidden-by-condition': 'masqué par une condition',
  'left-empty': 'laissé vide',
  'noyau-undetermined': 'noyau non déterminé',
};

/** « remplie » or « remplies »: French turns at two, `plural` of `walk-labels.ts` says why. */
function agreed(count: number, word: string): string {
  return count >= 2 ? `${word}s` : word;
}

/** What a cell of the stack says of its column, before its content. */
const STACK_LABEL =
  'max-md:before:pt-px max-md:before:font-semibold max-md:before:text-gray-500 max-md:before:text-micro max-md:before:uppercase max-md:before:tracking-wide max-md:before:content-[attr(data-label)]';

/** A cell of the table: its padding and rule, or nothing of its own in the stack. */
const CELL = 'border-gray-100 border-b px-4 py-2.5 align-top max-md:contents';

export function SessionVariables({ entries, retained, onOpenPage }: SessionVariablesProps) {
  if (entries.length === 0) {
    return (
      <p
        data-testid={SESSIONS_TEST_IDS.variablesEmpty}
        className="rounded-md border border-gray-200 px-4 py-6 text-gray-700"
      >
        Cette expérience ne déclare aucune variable.
      </p>
    );
  }

  const filled = entries.filter((entry) => entry.reason === null).length;
  const unfilled = entries.length - filled;

  return (
    <div data-testid={SESSIONS_TEST_IDS.variables} className="flex flex-col gap-2.5">
      <p
        data-testid={SESSIONS_TEST_IDS.variablesCount}
        data-filled={filled}
        data-unfilled={unfilled}
        className="text-caption text-gray-600"
      >
        <b className="font-bold text-gray-900 tabular-nums">{filled}</b> {agreed(filled, 'remplie')}{' '}
        · <b className="font-bold text-gray-900 tabular-nums">{unfilled}</b> non{' '}
        {agreed(unfilled, 'remplie')}
      </p>

      <table className="w-full border-collapse overflow-hidden rounded-md border border-gray-200 text-caption max-md:block max-md:border-0">
        <caption className="sr-only">
          Les variables que l’expérience déclare, remplies puis non remplies
        </caption>
        <thead className="max-md:hidden">
          <tr>
            {['Nom', 'Valeur', 'Nature', 'Page'].map((column) => (
              <th
                key={column}
                scope="col"
                className="border-gray-200 border-b bg-gray-50 px-4 py-2 text-left font-semibold text-gray-600 text-micro uppercase tracking-wide"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="max-md:block">
          {entries.map((entry, index) => {
            const empty = entry.reason !== null;
            // The served order puts every unfilled line after the filled ones,
            // so the first of them is the one right after the last filled.
            const first = empty && index === filled && filled > 0;
            return (
              <tr
                key={entry.key}
                data-testid={SESSIONS_TEST_IDS.variableRow}
                data-key={entry.key}
                data-kind={entry.kind}
                data-filled={empty ? 'false' : 'true'}
                data-reason={entry.reason ?? undefined}
                className={cn(
                  'max-md:mb-2 max-md:grid max-md:grid-cols-[max-content_minmax(0,1fr)] max-md:gap-x-3 max-md:gap-y-1 max-md:rounded-md max-md:border max-md:border-gray-200 max-md:px-3.5 max-md:py-3',
                  '[&:last-child>td]:border-b-0',
                  empty ? 'bg-gray-50 text-gray-500' : null,
                  // The thicker rule is the table's; the stack parts the two groups by a gap.
                  first ? 'max-md:mt-4 md:[&>td]:border-t-2 md:[&>td]:border-t-gray-200' : null,
                )}
              >
                <td
                  className={cn(
                    CELL,
                    // The one cell of the stack that stays a box: without its
                    // padding and rules, which belong to the table (maquette).
                    'whitespace-nowrap max-md:col-span-full max-md:mb-0.5 max-md:block max-md:whitespace-normal max-md:border-0 max-md:p-0',
                  )}
                >
                  <span
                    className={cn(
                      PILL_SHAPE_CLASS,
                      'text-caption leading-5 max-md:whitespace-normal max-md:[overflow-wrap:anywhere]',
                      entry.kind === 'noyau' ? 'bg-bleu-nuit text-white' : PILL_INK_CLASS.value,
                    )}
                  >
                    @{entry.name}
                  </span>
                </td>
                <td
                  data-label="Valeur"
                  className={cn(
                    CELL,
                    STACK_LABEL,
                    'max-w-[52ch] whitespace-pre-wrap [overflow-wrap:anywhere] max-md:max-w-none',
                    empty ? 'font-normal text-gray-500' : 'font-semibold text-gray-900',
                  )}
                >
                  <span>
                    <EntryValue entry={entry} retained={retained} />
                  </span>
                </td>
                <td
                  data-label="Nature"
                  className={cn(
                    CELL,
                    STACK_LABEL,
                    'whitespace-nowrap',
                    empty ? 'text-gray-500' : 'text-gray-700',
                  )}
                >
                  {VARIABLE_KIND_LABEL[entry.kind]}
                </td>
                <td
                  data-label="Page"
                  className={cn(CELL, STACK_LABEL, empty ? 'text-gray-500' : 'text-gray-700')}
                >
                  <EntryPage entry={entry} onOpenPage={onOpenPage} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="max-w-[80ch] text-caption text-gray-500">
        Remplies d’abord, dans l’ordre de la timeline ; puis déclarées et non remplies, avec leur
        raison. Une page reprise donne la valeur de sa dernière visite. Les objectifs n’y sont pas :
        leur matière vit dans la conversation et dans la Matière.
      </p>
    </div>
  );
}

/** The value of a line, whole - or the reason it has none. */
function EntryValue({
  entry,
  retained,
}: {
  readonly entry: SessionInventoryEntry;
  readonly retained: NoyauCode | null;
}) {
  if (entry.reason !== null) return <>{UNFILLED_REASON_LABEL[entry.reason]}</>;
  const { value } = entry;
  if (typeof value === 'boolean') {
    return value ? (
      <>
        {/* The tick of the walk's cards, beside a checked box only. */}
        <span aria-hidden="true" className="mr-1 text-vert-deep">
          ✓
        </span>
        coché
      </>
    ) : (
      <>non coché</>
    );
  }
  if (entry.kind === 'noyau' && value !== null) {
    const code = NOYAUX.find((noyau) => noyau.name === value)?.code ?? retained;
    if (code === null) return <>{value}</>;
    const noyau = noyauOf(code);
    return (
      <>
        <span
          aria-hidden="true"
          className="mr-1.5 inline-block size-2.5 rounded-full"
          style={{ backgroundColor: noyau.color }}
        />
        {noyau.name}
        <span className="ml-1.5 font-normal text-gray-500 text-micro">{code}</span>
      </>
    );
  }
  // A Note holds an integer from 1 to 5; Sessions words it « 4 / 5 », in text (#176).
  if (entry.kind === 'note' && value !== null) return <>{noteValueLabel(value)}</>;
  return <>{value}</>;
}

/** « 5 · Ce qui compte pour toi », a way back to its card - or a grey name without one. */
function EntryPage({
  entry,
  onOpenPage,
}: {
  readonly entry: SessionInventoryEntry;
  readonly onOpenPage: (visit: number) => void;
}) {
  const { visit, name } = entry.page;
  if (visit === null) {
    return (
      <span data-testid={SESSIONS_TEST_IDS.variablePage} className="text-gray-500">
        {name}
      </span>
    );
  }
  return (
    <button
      type="button"
      data-testid={SESSIONS_TEST_IDS.variablePage}
      data-visit={visit}
      onClick={() => onOpenPage(visit)}
      className="cursor-pointer text-left font-semibold text-bleu-nuit underline decoration-gray-300 underline-offset-[3px] hover:decoration-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* The rank of the card - « Page N » on the parcours. */}
      <span className="font-normal text-gray-600">{visit + 1} ·</span> {name}
    </button>
  );
}
